'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
function subscribeOsScheme(callback) {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
        return () => { };
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    mql.addEventListener('change', callback);
    return () => mql.removeEventListener('change', callback);
}
function getOsSchemeSnapshot() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
        return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
function getOsSchemeServerSnapshot() {
    return 'light';
}
/** Live OS `prefers-color-scheme`, used to resolve `'system'` to a concrete theme. */
function useOsScheme() {
    return useSyncExternalStore(subscribeOsScheme, getOsSchemeSnapshot, getOsSchemeServerSnapshot);
}
const ThemeContext = createContext({
    theme: 'system',
    resolvedTheme: 'light',
    setTheme: () => { },
});
/**
 * Owns the `data-theme` attribute on `<html>` (E4 §8.1). When the
 * preference is `'system'` the attribute is removed entirely so
 * `tokens.css`'s `@media (prefers-color-scheme: dark)` fallback drives the
 * theme with zero flash-of-wrong-theme on first paint; an explicit
 * light/dark preference always wins.
 */
export function ThemeProvider({ theme, onThemeChange, children }) {
    const osScheme = useOsScheme();
    const resolvedTheme = theme === 'system' ? osScheme : theme;
    useEffect(() => {
        const root = document.documentElement;
        if (theme === 'system') {
            root.removeAttribute('data-theme');
        }
        else {
            root.setAttribute('data-theme', theme);
        }
    }, [theme]);
    const value = useMemo(() => ({ theme, resolvedTheme, setTheme: onThemeChange }), [theme, resolvedTheme, onThemeChange]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
}
export function useTheme() {
    return useContext(ThemeContext);
}
